import type { TFunction } from 'i18next';
import { z } from 'zod';

import { getLocalTimeZone, isFutureIso, localDateTimeToISO } from '~/utils/date';

/**
 * The K2 New batch page's six sections, in sidebar/step order; kept in the URL (`?section=`).
 * Scope sits right after Feed products — it checks the scope that section defines.
 */
export const NEW_RUN_SECTIONS = [
  'destination',
  'products',
  'scope',
  'content',
  'start',
  'review',
] as const;

/** One New batch section id. */
export type NewRunSection = (typeof NEW_RUN_SECTIONS)[number];

/** The `useCase` post-step flags the New Run form exposes, in display order. */
export const NEW_RUN_POST_STEPS = [
  'scoringPreEnabled',
  'scoringPostEnabled',
  'gapAnalysisEnabled',
  'tagsTrendsEnabled',
  'searchDescriptorsEnabled',
  'phraseDescriptionsEnabled',
] as const;

/** One post-step flag key on `useCase`. */
export type NewRunPostStep = (typeof NEW_RUN_POST_STEPS)[number];

/** google-agent's retailer prompt slug rule (`UseCaseConfig.retailer` pattern + max length). */
export const RETAILER_SLUG = /^[a-z0-9-]*$/;
export const RETAILER_MAX_LENGTH = 64;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const POSITIVE_INT = /^\d+$/;

interface SchemaOptions {
  t: TFunction<'catalog-v2-runs'>;
  /** `maxProducts.minimum` from trigger-options (the server floor for a manual run). */
  maxProductsMinimum: number;
}

/** Blank, or a whole number at or above `min` (number inputs are kept as strings to avoid NaN). */
function optionalIntAtLeast(min: number, message: string) {
  return z
    .string()
    .trim()
    .refine((v) => v === '' || (POSITIVE_INT.test(v) && Number(v) >= min), { message });
}

/**
 * Zod schema for the New Run form, built from the server's own rules (the
 * `maxProducts` floor) so the form refuses what the trigger would 422. Values
 * stay in form shape; `toTriggerBody` turns them into the wire body.
 */
export function buildNewRunSchema({ t, maxProductsMinimum }: SchemaOptions) {
  const filterRow = z.object({
    kind: z.enum(['predefined', 'dynamic']),
    key: z.string().trim(),
    values: z.array(z.string()),
  });

  return z
    .object({
      connectorCode: z.string().min(1, t('newRun.errors.connectorRequired')),
      useCase: z.string().min(1, t('newRun.errors.useCaseRequired')),
      name: z.string().trim(),
      /** Blank = the connector's default flow. */
      flow: z.string(),
      /** Picked enrichment field codes (always-on ones excluded); empty = every field. */
      fields: z.array(z.string()),
      postSteps: z.object({
        scoringPreEnabled: z.boolean().optional(),
        scoringPostEnabled: z.boolean().optional(),
        gapAnalysisEnabled: z.boolean().optional(),
        tagsTrendsEnabled: z.boolean().optional(),
        searchDescriptorsEnabled: z.boolean().optional(),
        phraseDescriptionsEnabled: z.boolean().optional(),
      }),
      retailer: z
        .string()
        .trim()
        .max(RETAILER_MAX_LENGTH, t('newRun.errors.retailerTooLong', { max: RETAILER_MAX_LENGTH }))
        .regex(RETAILER_SLUG, t('newRun.errors.retailerPattern')),
      ingestMode: z.string(),
      maxProducts: optionalIntAtLeast(
        Math.max(1, maxProductsMinimum),
        t('newRun.errors.maxProducts', { min: Math.max(1, maxProductsMinimum) }),
      ),
      syncId: z
        .string()
        .trim()
        .refine((v) => v === '' || UUID.test(v), { message: t('newRun.errors.syncId') }),
      /** Source product ids, one per line. */
      productIds: z.string(),
      recordSkipped: z.boolean(),
      filters: z.array(filterRow),
      /** K2 "Start" section: `now` triggers immediately; `scheduled` creates a one-off schedule
       *  (`once`, api-changes §74) that fires at `startAt` — never a held run. */
      startMode: z.enum(['now', 'scheduled']),
      /** `<input type="datetime-local">` value in the viewer's zone; the mapper resolves it to UTC. */
      startAt: z.string(),
      /** "Run once" submits this form; "schedule" hands off to Schedules (no trigger body impact). */
      repeatMode: z.enum(['once', 'schedule']),
    })
    .superRefine((values, ctx) => {
      if (values.startMode !== 'scheduled') return;
      const iso = values.startAt ? localDateTimeToISO(values.startAt, getLocalTimeZone()) : null;
      if (!iso) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['startAt'],
          message: t('newRun.errors.startAtRequired'),
        });
        return;
      }
      // The picker is minute-precise and the server floors to the minute then requires the
      // result to be in the future — so the picked minute itself must still be ahead of now.
      if (!isFutureIso(iso)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['startAt'],
          message: t('newRun.errors.startAtTooSoon'),
        });
      }
    });
}

/** The New Run form's values (form shape, not wire shape). */
export type NewRunFormValues = z.infer<ReturnType<typeof buildNewRunSchema>>;

/** One filter row: a predefined key from trigger-filters, or a dynamic bronze column. */
export type NewRunFilterRow = NewRunFormValues['filters'][number];

/**
 * Which form fields belong to each K2 section — the sidebar's warning badge on a section lights
 * up when `form.formState.errors` has a key in that section's list. `review` has none of its own:
 * it only ever reflects the other four.
 */
export const NEW_RUN_SECTION_FIELDS: Record<NewRunSection, (keyof NewRunFormValues)[]> = {
  destination: ['connectorCode', 'useCase', 'flow', 'name'],
  products: ['ingestMode', 'maxProducts', 'syncId', 'productIds', 'filters', 'recordSkipped'],
  scope: [],
  content: ['fields', 'postSteps', 'retailer'],
  start: ['startMode', 'startAt', 'repeatMode'],
  review: [],
};

/** Empty form; connector-dependent defaults (use case, ingest mode) are applied on load. */
export const NEW_RUN_EMPTY_VALUES: NewRunFormValues = {
  connectorCode: '',
  useCase: '',
  name: '',
  flow: '',
  fields: [],
  postSteps: {},
  retailer: '',
  ingestMode: '',
  maxProducts: '',
  syncId: '',
  productIds: '',
  recordSkipped: false,
  filters: [],
  startMode: 'now',
  startAt: '',
  repeatMode: 'once',
};

/** Splits the product-ids textarea into trimmed, de-duplicated source ids. */
export function parseProductIds(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    ),
  ];
}
